import { formatNumberWithCommas } from '@utils';
import { FIELD_SIZE, INPUT_FIELD_TYPE, INPUT_TYPE } from '@components/DynamicFormField/constant';
import {
  getDashboardConfig,
  addNewAccountSettlement,
  addNewClientSettlement,
  addNewLedgerSettlement,
  updateAccountSettlement,
  updateClientSettlement,
} from '@store/actions/settlementServiceDashboardActions';
import { SEARCH_META_KEYS } from '@views/SettlementServiceDashboard/constant';
import { toSentenceCase } from '@utils';
import { toFixedNumber } from '@utils/numberUtils';
import { formatArrayAsDelimitedString } from '@utils/arrayUtils';


/**
 * Currency Configuration
 */
export const CURRENCY_OPTIONS = [
  {
    key: 'INR',
    value: 'INR',
    label: 'INR',
    isDisabled: false
  },
  {
    key: 'USD',
    value: 'USD',
    label: 'USD',
    isDisabled: true
  },
  {
    key: 'EUR',
    value: 'EUR',
    label: 'EUR',
    isDisabled: true
  },
  {
    key: 'GBP',
    value: 'GBP',
    label: 'GBP',
    isDisabled: true
  },
  {
    key: 'AED',
    value: 'AED',
    label: 'AED',
    isDisabled: true
  }
];

/**
 * General Configurations
 */
const formFieldToDynamicFormField = (fieldConfig) => {
  return {
    ...fieldConfig,
    key: fieldConfig?.name,
    name: fieldConfig?.name,
    value: null,
    label: `${fieldConfig?.title}`,
    caption: null,
    isEditable: fieldConfig?.isEditable,
    isRequired: fieldConfig?.isRequired,
    fieldType: fieldConfig?.type,
    inputType: fieldConfig?.inputType || 'text',
    fieldSize: 'sm',
    width: 'auto'
  };
};

export const entityToFormData = ({entity}) => {
  const formData = {};
  entity.map((detail) => {
    if (detail.isCreatable) {
      if (detail.type === 'cards') {
        formData[detail.name] = detail.data.map((data) => {
          const value = {};
          Object.keys(data).map((key) => (value[key] = data[key].value));
          return value;
        });
      }
      else {
        formData[detail.name] = detail.data;
      }
    } else {
      detail.data.map((item) => {
        if (item.isCreatable) {
          formData[item.name] = item.value === '-' ? '' : item.value;
        }
      });
    }
  });
  return formData;
};

const searchBuilderBySearchKeys = ({ searchKeys }) => {
  return searchKeys.map((key, index) => {
    const meta = SEARCH_META_KEYS[key] || {
      display: toSentenceCase(key),
      placeholder: `Search by ${toSentenceCase(key)}`
    };

    return {
      ...meta,
      searchKey: key,
      isDefault: index === 0
    };
  });
};

/**
 * General Config
 */
export const generalConfig = {
  searchBuilderBySearchKeys: ({searchKeys}) => searchBuilderBySearchKeys({searchKeys}),
  getDashboardConfig:({ onSuccess, onComplete }) => getDashboardConfig({ onSuccess, onComplete })
}

// Client Related Utils
export const getClientSettlementTable = (client) => {
  return [
    {
      title: 'Client Details',
      data: [
        {
          name: 'Client ID: ',
          value: client?.clientId || 'N/A',
          isCopyable: true
        },
        {
          name: 'Total Amount to Settle: ',
          value: formatNumberWithCommas(client?.totalAmountToSettle) || 'N/A',
        }
      ]
    }
  ];
};

export const getClientAccountTable = (account) => {
  return [
    {
      title: 'Client Accounts',
      data: [
        {
          name: 'Account Id: ',
          value: account?.accountId || '-',
          isCopyable: true
        },
        {
          name: 'Account Name: ',
          value: account?.accountName || '-',
          isCopyable: true
        },
        {
          name: 'Account Iban: ',
          value: account?.accountIban || '-',
          isCopyable: true
        },
        {
          name: 'Account Lei: ',
          value: account?.accountLei || '-',
          isCopyable: true
        },
        {
          name: 'isDefault: ',
          value: JSON.stringify(account?.isDefault)?.toUpperCase() || '-'
        },
        {
          name: 'Amount to Settle: ',
          value: formatNumberWithCommas(account?.amountToSettle) || '-'
        },
        {
          name: 'Payout Partner: ',
          value: account?.payoutPartners && formatArrayAsDelimitedString({ array: account?.payoutPartners, delimiter: ", "}) || "-"
        }
      ]
    }
  ];
};

const getNewClientSettlementFieldsConfig = (commonData = {}) => {
  // Transform accountIds to dropdown options
  const accountIdOptions = commonData?.accountIds?.map(accountId => ({
    key: accountId,
    value: accountId,
    label: accountId,
    isDisabled: false
  })) || [];

  // Transform payoutPartners to dropdown options
  const payoutPartnersOptions = commonData?.payoutPartners?.map(partner => ({
    key: partner,
    value: partner,
    label: partner,
    isDisabled: false
  })) || [];

  return [
    {
      key: "clientId",
      name: "clientId",
      value: null,
      label: `Client ID`,
      caption: null,
      placeholder: "Enter Client ID",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    },
    {
      key: "accountId",
      name: "accountId",
      value: null,
      label: `Account ID`,
      caption: null,
      placeholder: "Select Account ID",
      fieldType: INPUT_FIELD_TYPE.SELECT,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
      options: accountIdOptions,
    },
    {
      key: "currency",
      name: "currency",
      value: null,
      label: `Currency`,
      caption: null,
      placeholder: "Select Currency",
      fieldType: INPUT_FIELD_TYPE.SELECT,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
      options: CURRENCY_OPTIONS,
    },
    {
      key: "isDefault",
      name: "isDefault",
      value: null,
      label: `Is Default`,
      caption: null,
      fieldType: INPUT_FIELD_TYPE.SWITCH,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: false,
    },
    {
      key: "payoutPartners",
      name: "payoutPartners",
      value: [],
      label: `Payout Partners`,
      caption: null,
      placeholder: "Select Payout Partners",
      fieldType: INPUT_FIELD_TYPE.DRAG_DROP,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
      options: payoutPartnersOptions,
    }
  ];
};

export const formDataToAddClientSettlement = ({formData}) => {
  return {
    clientId: formData.clientId,
    accountId: formData.accountId,
    currency: formData.currency,
    isDefault: formData.isDefault,
    payoutPartners: formData.payoutPartners
  };
};

export const clientSettlementsConfig = {
  title: 'Client Settlements',
  id: 'clientSettlements',
  getClientSettlementTable: (client) => getClientSettlementTable(client),
  getClientAccountTable: (account) => getClientAccountTable(account),
  updateClientSettlement: ({ clientId, payload, onSuccess, onComplete }) =>
    updateClientSettlement({ clientId, payload, onSuccess, onComplete}),
  newClientSettlementFieldsConfig: (commonData) => getNewClientSettlementFieldsConfig(commonData),
  addNewClientSettlement: ({ payload, onSuccess, onComplete }) =>
    addNewClientSettlement({ payload, onSuccess, onComplete}),
  addClientSettlementPayloadConvertor: ({ formData }) => formDataToAddClientSettlement({ formData })
};


/**
 * Account Settlement Configurations
 */
const getNewAccountSettlementFieldsConfig = () => {
  return [
    {
      key: "accountId",
      name: "accountId",
      value: null,
      label: `Account ID`,
      caption: null,
      placeholder: "Enter Account ID",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    },
    {
      key: "accountName",
      name: "accountName",
      value: null,
      label: `Account Name`,
      caption: null,
      placeholder: "Enter the Account Name",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    },
    {
      key: "accountIban",
      name: "accountIban",
      value: null,
      label: `Account IBAN`,
      caption: null,
      placeholder: "Enter the Account IBAN",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    },
    {
      key: "accountLei",
      name: "accountLei",
      value: null,
      label: `Account LEI`,
      caption: null,
      placeholder: "Enter the Account LEI",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: false,
    }
  ];
};

const accountSettlementToEntity = (accountSettlement) => {
  return {
    data: [
      {
        title: 'Account ID',
        value: accountSettlement?.accountId,
        isCopyable: true,
        showEditIcon: true,
        position: {
          value: 'sticky',
          style: {
            position: 'sticky',
            zIndex: '1',
            left: '0',
            backgroundColor: '#111111',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)'
          }
        },
        isCreatable: true,
        isEditable: false,
        name: 'accountId',
      },
      {
        title: 'Account IBAN',
        value: accountSettlement?.accountIban,
        maskValue: accountSettlement?.accountIban,
        isCreatable: true,
        isEditable: false,
        name: 'accountIban'
      },
      {
        title: 'Account Name',
        value: accountSettlement?.accountName,
        maskValue: accountSettlement?.accountName,
        type: 'input',
        inputType: 'string',
        isCreatable: true,
        isEditable: true,
        name: 'accountName'
      },
      {
        title: 'Account LEI',
        value: accountSettlement?.accountLei,
        maskValue: accountSettlement?.accountLei || '-',
        type: 'input',
        inputType: 'string',
        isCreatable: true,
        isEditable: true,
        name: 'accountLei'
      },
    ],
    type: 'object',
    id: `${accountSettlement?.accountId}`,
  };
};

export const accountSettlementsToEntity = (accountSettlement) => {
  return accountSettlement.map(accountSettlementToEntity);
};

export const formDataToUpdateAccountSettlement = ({formData}) => {
  return {
    accountName: formData?.accountName,
    accountLei: formData?.accountLei
  };
};

export const formDataToAddAccountSettlement = ({formData}) => {
  return {
    clientId: formData?.clientId,
    accountId: formData?.accountId,
    accountName: formData?.accountName,
    accountIban: formData?.accountIban,
    accountLei: formData?.accountLei,
  };
};

export const accountSettlementsConfig = {
  title: 'Account Settlements',
  id: 'accountSettlements',
  entityConvertor: (data) => accountSettlementsToEntity(data),
  toDynamicFormField: (field) => formFieldToDynamicFormField(field),
  entityToFormData: ({entity, timeUnit}) => entityToFormData({entity}),
  updateAccountSettlementPayloadConvertor: ({formData}) => formDataToUpdateAccountSettlement({formData}),
  updateAccountSettlement: ({accountId, payload, onSuccess, onComplete}) =>
    updateAccountSettlement({ accountId, payload, onSuccess, onComplete}),
  newConfigFieldsConfig: () => getNewAccountSettlementFieldsConfig(),
  addAccountSettlementPayloadConvertor: ({formData}) => formDataToAddAccountSettlement({formData}),
  addNewAccountSettlement: ({ payload, onSuccess, onComplete }) =>
    addNewAccountSettlement({ payload, onSuccess, onComplete}),
};

/**
 * Ledger Settlement Configurations
 */
const getNewLedgerSettlementFieldsConfig = (commonData = {}) => {
  // Transform accountIds to dropdown options
  const accountIdOptions = commonData?.accountIds?.map(accountId => ({
    key: accountId,
    value: accountId,
    label: accountId,
    isDisabled: false
  })) || [];

  return [
    {
      key: "clientId",
      name: "clientId",
      value: null,
      label: `Client ID`,
      caption: null,
      placeholder: "Enter Client ID (DIGIT9)",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    },
    {
      key: "accountId",
      name: "accountId",
      value: null,
      label: `Account ID`,
      caption: null,
      placeholder: "Select Account ID",
      fieldType: INPUT_FIELD_TYPE.SELECT,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
      options: accountIdOptions,
    },
    {
      key: "amount",
      name: "amount",
      value: null,
      label: `Amount`,
      caption: null,
      placeholder: "Enter Amount (e.g., 100.00)",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.NUMBER,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    },
    {
      key: "referenceId",
      name: "referenceId",
      value: null,
      label: `Reference ID`,
      caption: null,
      placeholder: "Enter Reference ID (e.g., adjustment-20250515)",
      fieldType: INPUT_FIELD_TYPE.INPUT,
      inputType: INPUT_TYPE.STRING,
      fieldSize: FIELD_SIZE.MEDIUM,
      width: 'auto',
      isEditable: true,
      isRequired: true,
    }
  ];
};

const ledgerSettlementToEntity = (ledgerSettlement) => {
  return {
    data: [
      {
        title: 'Client ID',
        value: ledgerSettlement?.clientId,
        showEditIcon: true,
        isCopyable: true,
        position: {
          value: 'sticky',
          style: {
            position: 'sticky',
            zIndex: '1',
            left: '0',
            backgroundColor: '#111111',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)'
          }
        },
        isCreatable: true,
        isEditable: false,
        name: 'clientId',
      },
      {
        title: 'Account ID',
        value: ledgerSettlement?.accountId,
        isCreatable: true,
        isEditable: true,
        name: 'accountId',
      },
      {
        title: 'Amount',
        value: formatNumberWithCommas(ledgerSettlement?.amount),
        isCreatable: true,
        isEditable: true,
        name: 'amount'
      },
      {
        title: 'Reference ID',
        value: ledgerSettlement?.referenceId,
        isCreatable: true,
        isEditable: true,
        name: 'referenceId'
      },
      {
        title: 'Created At',
        value: ledgerSettlement?.createdAt,
        isCreatable: true,
        isEditable: false,
        name: 'createdAt'
      },
      {
        title: 'Status',
        value: ledgerSettlement?.status,
        isCreatable: true,
        isEditable: false,
        name: 'status'
      }
    ],
    type: 'object',
    id: `${ledgerSettlement?.clientId}`,
  };
};

export const ledgerSettlementsToEntity = (ledgerSettlement) => {
  return ledgerSettlement.map(ledgerSettlementToEntity);
};

export const formDataToAddLedgerSettlement = ({formData}) => {
  return {
    clientId: formData.clientId,
    accountId: formData.accountId,
    amount: toFixedNumber(formData.amount, 2), // Convert to number and format to 2 decimal places
    referenceId: formData.referenceId
  };
};

export const ledgerSettlementsConfig = {
  title: 'Ledger Settlements',
  id: 'ledgerSettlements',
  entityConvertor: (data) => ledgerSettlementsToEntity(data),
  newConfigFieldsConfig: (commonData) => getNewLedgerSettlementFieldsConfig(commonData),
  addLedgerSettlementPayloadConvertor: ({formData}) => formDataToAddLedgerSettlement({formData}),
  addNewLedgerSettlement: ({ payload, onSuccess, onComplete }) =>
    addNewLedgerSettlement({ payload, onSuccess, onComplete}),
};
